/**
 * Διαχείριση καταστήματος → «Κατάλογος» (Φάση 3, mock εγκεκριμένο 12/10/2026): στη θέση των
 * «Προϊόντα» (/products) και «Υλικά». Καρτέλες: Προϊόντα · Ομάδες επιλογών · Ομάδες μεγεθών · Υλικά
 * (οι βιβλιοθήκες είναι του components/catalog/libraries). Στα Προϊόντα: δέντρο | λίστα | πάνελ.
 *
 * Αποφάσεις ιδιοκτήτη: (1) «Διαθέσιμο» και «Κρυφό» της λίστας αποθηκεύονται ΑΜΕΣΑ· όλα τα άλλα σε
 * ΜΙΑ μπάρα «Αποθήκευση»· (2) κοινή ομάδα επιλογών δεν αλλάζει μέσα από το προϊόν· (3) καμία στήλη
 * efood/Wolt στα μεγέθη. Προϊόν δεν σβήνεται ποτέ: «Απόσυρση» = μη διαθέσιμο + κρυφό.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Package, RefreshCw, Upload, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import ConfirmModal from '../shared/ConfirmModal';
import CatalogImportWizard from '../CatalogImportWizard';
import { registerDirtySource } from '../../utils/unsavedChanges';
import type { StateFeeRow } from '../../utils/feeForm';
import OptionGroupsTab from './libraries/OptionGroupsTab';
import SizeGroupsTab from './libraries/SizeGroupsTab';
import IngredientsTab from './libraries/IngredientsTab';
import { createIngredient, createOptionGroup, createSizeGroup } from './libraries/api';
import { confirmLeaveLibrary } from './libraries/draftGuard';
import { catalogApi, errorMessage } from './catalogApi';
import {
  ApiCategory, ApiOptionGroup, ApiProduct, ApiSizeGroup, ApiStoreIngredient, CatRef, EMPTY_CATEGORY_FORM, FormSize,
  GroupRef, IngRef, VatCategory, catKeyOfRef, emptyProductForm, newUid, productKey, refOfCatKey,
} from './catalogModel';
import {
  CatalogDraft, addNewCategory, addNewGroup, addNewIngredient, addNewProduct, addNewSizeGroup, baseProduct, buildBase,
  currentCategoryForm, currentProduct, currentTree, emptyDraft, moveCategory, moveProducts, orderedProducts,
  remainingDraft, removeNewCategory, removeNewProduct, setProductOrder, tempKey, updateCategory, updateProduct,
  updateProductForm,
} from './catalogDraft';
import {
  ChangeContext, ChangeEntry, PanelTab, applyRevert, changeOwners, changedFields, changedTabs, computeChanges, editedOwners,
} from './catalogChanges';
import {
  ListFilter, ProductView, allInTreeOrder, allViews, categoryTone, matchesSearch, membersByNode, passesFilter, treeView,
} from './catalogView';
import { saveDraft, validateDraft } from './catalogSave';
import CatalogTree, { TreeRail } from './CatalogTree';
import ProductList, { ListSection } from './ProductList';
import ProductPanel from './ProductPanel';
import CategoryPanel from './CategoryPanel';
import BulkPanel from './BulkPanel';
import CatalogSaveBar from './CatalogSaveBar';
import { CategoryOption } from './ProductBasicsTab';
import { GroupInfo, NewGroupDraft, groupInfo, newGroupInfo } from './ProductOptionsTab';
import { useDragState } from './dragState';
import { ToneDot } from './ui';
import './Catalog.css';

type View = 'products' | 'options' | 'sizes' | 'ingredients';
type Selection = { kind: 'product' | 'category'; key: string } | null;
type LoadState = 'loading' | 'ready' | 'error';
type StateFee = StateFeeRow & { title?: string; short_label?: string };
/** Επιβεβαίωση για κρατικό τέλος (η αυτόματη γραμμή τέλους πλαστικού στηρίζεται σε αυτό). */
type FeeConfirm = { key: string; action: 'unavailable' | 'hide' } | null;

// Πλάτη του χώρου εργασίας (όχι του παραθύρου: η εφαρμογή κάνει zoom σε μικρές οθόνες).
const WIDE_WS = 1030;     // ≈ 1320 px παράθυρο με το πλαϊνό μενού: κάτω από αυτό το δέντρο μαζεύεται με ανοιχτό πάνελ
const NARROW_WS = 712;    // ≈ 1000 px παράθυρο: κάτω από αυτό το πάνελ αιωρείται πάνω από τη λίστα
const TINY_WS = 640;      // κινητό: το δέντρο μαζεύεται πάντα
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const isFeeView = (v: ProductView | null | undefined) => !!v && v.feeCategory !== null && v.feeCategory !== undefined;

const CatalogPage: React.FC = () => {
  const { store } = useAuth();
  const { t } = useTranslation(['catalog', 'products', 'common']);
  const { showToast } = useToast();
  const storeId = store?.id;
  // Κεντρικός κατάλογος: μόνο ανάγνωση (και ο server κόβει τις εγγραφές με CATALOG_CENTRAL).
  const central = store?.catalog_management === 'central';
  const warehouse = store?.warehouse !== false;
  const lockTitle = central ? t('catalog:central.hint') : undefined;
  const drag = useDragState();

  /* ───────── δεδομένα server ───────── */
  const [load, setLoad] = useState<LoadState>('loading');
  const [categoryList, setCategoryList] = useState<ApiCategory[]>([]);
  const [products, setProducts] = useState<ApiProduct[]>([]);
  const [groups, setGroups] = useState<ApiOptionGroup[]>([]);
  const [sizeGroups, setSizeGroups] = useState<ApiSizeGroup[]>([]);
  const [ingredients, setIngredients] = useState<ApiStoreIngredient[]>([]);
  const [vatCategories, setVatCategories] = useState<VatCategory[]>([]);
  const [stateFees, setStateFees] = useState<StateFee[]>([]);
  const [suppliers, setSuppliers] = useState<Array<{ id: number; name: string }>>([]);
  const [links, setLinks] = useState<Record<string, GroupRef[]>>({});
  const [linkOrders, setLinkOrders] = useState<Record<string, number[]>>({});
  const [catGroups, setCatGroups] = useState<Record<string, GroupRef[]>>({});

  /* ───────── οθόνη ───────── */
  const [view, setView] = useState<View>('products');
  const [node, setNode] = useState<string>('all');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [selection, setSelection] = useState<Selection>(null);
  const [checked, setChecked] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ListFilter>('all');
  const [panelTab, setPanelTab] = useState<PanelTab>('sizes');
  const [treeMode, setTreeMode] = useState<'auto' | 'open' | 'closed'>('auto');
  const [drawer, setDrawer] = useState(false);
  const [draft, setDraft] = useState<CatalogDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [withdrawKey, setWithdrawKey] = useState<string | null>(null);
  const [feeConfirm, setFeeConfirm] = useState<FeeConfirm>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [busy, setBusy] = useState<Set<string>>(() => new Set());
  const [loadVersion, setLoadVersion] = useState(0);
  const [manualStart, setManualStart] = useState(false);

  const core = useMemo(() => buildBase(products, categoryList, ingredients), [products, categoryList, ingredients]);
  const base = useMemo(() => ({ ...core, links, catGroups }), [core, links, catGroups]);
  const baseRef = useRef(base);
  baseRef.current = base;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // Ενημερώνονται ΑΜΕΣΩΣ μετά από κάθε φόρτωση (όχι στο επόμενο render): η Αποθήκευση τα διαβάζει.
  const linksRef = useRef(links);
  const linkOrdersRef = useRef(linkOrders);
  const loadingLinks = useRef(new Map<string, Promise<void>>());

  /* ───────── φόρτωση ───────── */
  /**
   * Οι ομάδες επιλογών κάθε προϊόντος (λίστα ανά προϊόν· φορτώνονται όταν χρειαστούν). Δίνει ΜΟΝΟ ό,τι
   * ξέρει σίγουρα: ένα προϊόν με άγνωστες ομάδες δεν αλλάζει ομάδες (αλλιώς η Αποθήκευση θα αποσύνδεε
   * όσες δεν φορτώθηκαν).
   */
  const ensureLinks = useCallback(async (keys: string[]): Promise<Record<string, GroupRef[]>> => {
    const wanted = Array.from(new Set(keys.filter(k => k.startsWith('p:'))));
    const missing = wanted.filter(k => !linksRef.current[k] && !loadingLinks.current.has(k));
    for (let i = 0; i < missing.length; i += 4) {
      const batch = missing.slice(i, i + 4);
      batch.forEach(k => {
        loadingLinks.current.set(k, catalogApi.productGroups(Number(k.slice(2))).then(list => {
          linksRef.current = { ...linksRef.current, [k]: list.map(g => g.id) };
          linkOrdersRef.current = { ...linkOrdersRef.current, [k]: list.map(g => Number((g as ApiOptionGroup & { link_order?: number }).link_order) || 0) };
          setLinks(linksRef.current);
          setLinkOrders(linkOrdersRef.current);
        }).catch(() => undefined).finally(() => { loadingLinks.current.delete(k); }));
      });
      await Promise.all(batch.map(k => loadingLinks.current.get(k)));
    }
    await Promise.all(wanted.map(k => loadingLinks.current.get(k)).filter(Boolean));
    const out: Record<string, GroupRef[]> = {};
    wanted.forEach(k => { if (linksRef.current[k]) out[k] = linksRef.current[k]; });
    return out;
  }, []);

  const loadAll = useCallback(async (quiet = false): Promise<boolean> => {
    if (!storeId) return false;
    if (!quiet) setLoad('loading');
    try {
      const [cats, prods] = await Promise.all([catalogApi.categories(storeId), catalogApi.products(storeId)]);
      // Βιβλιοθήκες και λεξικά: αν κάποιο λείπει, η σελίδα δουλεύει χωρίς αυτό (όπως το παλιό παράθυρο).
      const [og, sg, ing, vat, fees, sup] = await Promise.all([
        catalogApi.optionGroups(storeId).catch(() => [] as ApiOptionGroup[]),
        catalogApi.sizeGroups(storeId).catch(() => [] as ApiSizeGroup[]),
        warehouse ? Promise.resolve([] as ApiStoreIngredient[]) : catalogApi.ingredients(storeId).catch(() => [] as ApiStoreIngredient[]),
        catalogApi.vatCategories().catch(() => [] as VatCategory[]),
        catalogApi.stateFees().catch(() => [] as StateFee[]),
        warehouse ? catalogApi.suppliers().catch(() => [] as Array<{ id: number; name: string }>) : Promise.resolve([] as Array<{ id: number; name: string }>),
      ]);
      setCategoryList(cats);
      setProducts(prods);
      setGroups(og);
      setSizeGroups(sg);
      setIngredients(ing);
      setVatCategories(vat);
      // Το unit_gross μένει όπως ήρθε (string NUMERIC)· λείπει σε backend πριν τη Φ1.
      setStateFees(fees.map(f => ({ ...f, unit_price: Number(f.unit_price) })));
      setSuppliers(sup);
      linksRef.current = {};
      linkOrdersRef.current = {};
      setLinks({});
      setLinkOrders({});
      setCatGroups({});
      loadingLinks.current.clear();
      setLoad('ready');
      setLoadVersion(v => v + 1);
      // Πρόχειρο με αλλαγμένες ομάδες: ξανά οι ομάδες του server, για τη σύγκριση.
      const pending = Object.keys(draftRef.current.products).filter(k => draftRef.current.products[k].groups);
      if (pending.length > 0) ensureLinks(pending);
      return true;
    } catch {
      if (!quiet) setLoad('error');
      return false;
    }
  }, [storeId, warehouse, ensureLinks]);

  useEffect(() => {
    setDraft(emptyDraft());
    setSelection(null);
    setChecked([]);
    setNode('all');
    loadAll();
  }, [loadAll]);

  /** Οι ομάδες που μπαίνουν αυτόματα στα νέα προϊόντα μιας κατηγορίας. */
  const ensureCatGroups = useCallback(async (key: string): Promise<GroupRef[]> => {
    const fromDraft = draftRef.current.categoryGroups[key];
    if (fromDraft) return fromDraft;
    if (!key.startsWith('c:')) return [];
    const cached = baseRef.current.catGroups[key];
    if (cached) return cached;
    try {
      const rows = await catalogApi.categoryGroups(Number(key.slice(2)));
      const ids = rows.map(g => g.id);
      setCatGroups(prev => ({ ...prev, [key]: ids }));
      return ids;
    } catch {
      return [];
    }
  }, []);

  /* ───────── παράγωγα ───────── */
  const views = useMemo(() => allViews(draft, base), [draft, base]);
  const members = useMemo(() => membersByNode(views, base, draft), [views, base, draft]);
  const nodes = useMemo(() => treeView(draft, base, members), [draft, base, members]);
  const tree = currentTree(draft, base);

  const nodeName = useCallback((key: string | null): string => {
    if (!key || key === 'all') return t('catalog:tree.all');
    if (key === 'none') return t('catalog:tree.none');
    return currentCategoryForm(draft, base, key)?.name || t('catalog:category.untitled');
  }, [draft, base, t]);

  const parentKeyOf = useCallback((key: string): string | null => {
    const keys = Object.keys(tree.children);
    for (let i = 0; i < keys.length; i += 1) if (tree.children[keys[i]].includes(key)) return keys[i];
    return null;
  }, [tree]);

  const categoryPath = useCallback((ref: CatRef): string => {
    const key = catKeyOfRef(ref);
    if (key === 'none') return t('catalog:tree.none');
    if (!currentCategoryForm(draft, base, key)) return t('catalog:product.unknownCategory', { id: ref });
    const parent = parentKeyOf(key);
    return parent ? `${nodeName(parent)} › ${nodeName(key)}` : nodeName(key);
  }, [draft, base, nodeName, parentKeyOf, t]);

  const groupInfos: GroupInfo[] = useMemo(
    () => [...groups.map(groupInfo), ...draft.newGroups.map(newGroupInfo)],
    [groups, draft.newGroups],
  );
  const groupName = useCallback((ref: GroupRef) => groupInfos.find(g => String(g.ref) === String(ref))?.name || `#${ref}`, [groupInfos]);

  const changeCtx: ChangeContext = useMemo(() => ({
    t,
    categoryPath,
    nodeName,
    groupName,
    supplierName: (id: number | undefined) => (id ? suppliers.find(s => s.id === id)?.name || `#${id}` : '—'),
    stateFees,
  }), [t, categoryPath, nodeName, groupName, suppliers, stateFees]);

  const changes = useMemo(() => (load === 'ready' ? computeChanges(draft, base, changeCtx) : []), [draft, base, changeCtx, load]);
  const dirty = changes.length > 0;
  const edited = useMemo(() => editedOwners(changes), [changes]);

  // Η έξοδος από τη σελίδα (άλλη καρτέλα, αποσύνδεση, F5) ρωτά πρώτα — utils/unsavedChanges.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => registerDirtySource('catalog', () => dirtyRef.current), []);

  const categoryOptions: CategoryOption[] = useMemo(() => {
    const out: CategoryOption[] = [{ ref: 0, label: t('catalog:product.noCategory') }];
    tree.roots.forEach(root => {
      out.push({ ref: refOfCatKey(root), label: nodeName(root) });
      (tree.children[root] || []).forEach(child => out.push({ ref: refOfCatKey(child), label: `${nodeName(root)} › ${nodeName(child)}` }));
    });
    return out;
  }, [tree, nodeName, t]);

  /* ───────── διάταξη: πλάτος χώρου εργασίας, ύψος ως το κάτω μέρος της οθόνης ───────── */
  const pageRef = useRef<HTMLDivElement>(null);
  const [wsEl, setWsEl] = useState<HTMLDivElement | null>(null);
  const [wsWidth, setWsWidth] = useState(0);
  const [top, setTop] = useState(0);
  useLayoutEffect(() => {
    const measureTop = () => {
      let y = 0;
      for (let n: HTMLElement | null = pageRef.current; n; n = n.offsetParent as HTMLElement | null) y += n.offsetTop;
      setTop(y);
    };
    measureTop();
    window.addEventListener('resize', measureTop);
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measureTop) : null;
    ro?.observe(document.body);
    return () => { window.removeEventListener('resize', measureTop); ro?.disconnect(); };
  }, []);
  useLayoutEffect(() => {
    if (!wsEl) return undefined;
    const measure = () => setWsWidth(wsEl.clientWidth);
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(wsEl);
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); };
  }, [wsEl]);

  const panelOpen = checked.length > 0 || !!selection;
  const known = wsWidth > 0;
  const autoCollapse = known && ((panelOpen && wsWidth < WIDE_WS) || wsWidth < TINY_WS);
  const treeCollapsed = treeMode === 'closed' || (treeMode === 'auto' && autoCollapse);
  const floating = known && wsWidth < NARROW_WS;
  const treeW = treeCollapsed ? 42 : known ? Math.round(clamp(wsWidth * 0.2, 220, 264)) : 240;
  const panelW = known ? Math.round(clamp(wsWidth * 0.44, 440, 600)) : 480;
  const columns = panelOpen && !floating ? `${treeW}px minmax(0,1fr) ${panelW}px` : `${treeW}px minmax(0,1fr)`;

  useEffect(() => { if (!treeCollapsed) setDrawer(false); }, [treeCollapsed]);

  /* ───────── λίστα του κόμβου ───────── */
  const searching = search.trim() !== '';
  const nodeInfo = useMemo(() => {
    const find = nodes.find(n => n.key === node) || nodes.flatMap(n => n.children).find(c => c.key === node) || null;
    return find;
  }, [nodes, node]);

  const sections: ListSection[] = useMemo(() => {
    const rowsOf = (keys: string[]) => keys.map(k => views[k]).filter(Boolean).filter(v => passesFilter(v, filter));
    if (searching) {
      const all = allInTreeOrder(draft, base, members).map(k => views[k]).filter(v => v && matchesSearch(v, search));
      return [{ node: '', rows: all.filter(v => passesFilter(v, filter)) }];
    }
    if (node === 'all') return [{ node: '', rows: rowsOf(allInTreeOrder(draft, base, members)), empty: t('catalog:list.emptyAll') }];
    if (node === 'none') return [{ node: 'none', rows: rowsOf(orderedProducts(draft, base, 'none', members.none || [])), empty: t('catalog:list.emptyNone') }];
    const kids = tree.children[node] || [];
    const own = orderedProducts(draft, base, node, members[node] || []);
    if (kids.length === 0) return [{ node, rows: rowsOf(own), empty: t('catalog:list.emptyCategory', { name: nodeName(node) }) }];
    const out: ListSection[] = [];
    if (own.length > 0) out.push({ node, title: nodeName(node), tone: nodeInfo?.tone, count: own.length, rows: rowsOf(own) });
    kids.forEach(kid => {
      const list = orderedProducts(draft, base, kid, members[kid] || []);
      const info = nodeInfo?.children.find(c => c.key === kid);
      out.push({ node: kid, title: nodeName(kid), tone: info?.tone, count: list.length, rows: rowsOf(list), empty: t('catalog:list.emptySub') });
    });
    return out;
  }, [searching, search, node, views, members, draft, base, tree, filter, nodeName, nodeInfo, t]);

  const footer = useMemo(() => {
    const all = sections.flatMap(s => s.rows);
    return { total: all.length, unavailable: all.filter(v => !v.is_available).length, hidden: all.filter(v => v.is_hidden).length };
  }, [sections]);

  // Οι ομάδες του κόμβου, για τη στήλη «Επιλογές» της λίστας.
  useEffect(() => {
    if (load !== 'ready' || searching || node === 'all') return;
    const keys = sections.flatMap(s => s.rows.map(r => r.key)).filter(k => k.startsWith('p:'));
    if (keys.length > 0 && keys.length <= 80) ensureLinks(keys);
  }, [load, searching, node, sections, ensureLinks]);

  useEffect(() => { if (checked.length > 0) ensureLinks(checked); }, [checked, ensureLinks, loadVersion]);
  useEffect(() => { if (selection?.kind === 'product') ensureLinks([selection.key]); }, [selection, ensureLinks, loadVersion]);
  useEffect(() => {
    if (selection?.kind !== 'category' || !selection.key.startsWith('c:')) return;
    ensureCatGroups(selection.key);
  }, [selection, ensureCatGroups, loadVersion]);

  /* ───────── καρτέλες σελίδας ───────── */
  const switchView = (next: View) => {
    if (next === view) return;
    // Η ανοιχτή κάρτα μιας βιβλιοθήκης χάνεται όταν κλείσει η καρτέλα της (το πρόχειρο των προϊόντων όχι).
    if (view !== 'products' && !confirmLeaveLibrary()) return;
    // Πίσω στα προϊόντα: ό,τι άλλαξε στις βιβλιοθήκες (ονόματα ομάδων, τιμές υλικών) ξαναδιαβάζεται.
    if (view !== 'products' && next === 'products') loadAll(true);
    setView(next);
  };

  /* ───────── ενέργειες: δέντρο & λίστα ───────── */
  const selectNode = (key: string) => {
    setNode(key);
    setSearch('');
    setDrawer(false);
    const parent = parentKeyOf(key);
    setExpanded(prev => {
      const next = new Set(prev);
      if ((tree.children[key] || []).length > 0) next.add(key);
      if (parent) next.add(parent);
      return next;
    });
  };

  const toggleExpanded = (key: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const openProduct = (key: string) => {
    setSelection({ kind: 'product', key });
    setChecked([]);
  };

  const editCategory = (key: string) => {
    setSelection({ kind: 'category', key });
    setChecked([]);
    selectNode(key);
  };

  const closePanel = () => {
    setSelection(null);
    setChecked([]);
  };

  const toggleCheck = (key: string) => setChecked(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

  /** Τα προϊόντα που έπεσαν σε κατηγορία (σύρσιμο στο δέντρο ή σε άλλη ομάδα της λίστας). */
  const moveTo = (keys: string[], target: string, index?: number) => {
    const ref = target === 'none' ? 0 : refOfCatKey(target);
    setDraft(d => {
      let next = moveProducts(d, baseRef.current, keys, ref);
      if (index !== undefined && target !== 'none') {
        const nextViews = allViews(next, baseRef.current);
        const memberKeys = membersByNode(nextViews, baseRef.current, next)[target] || [];
        const ordered = orderedProducts(next, baseRef.current, target, memberKeys).filter(k => !keys.includes(k));
        ordered.splice(Math.min(index, ordered.length), 0, ...keys);
        next = setProductOrder(next, target, ordered);
      }
      return next;
    });
    showToast('info', t('catalog:list.moved', { count: keys.length, name: nodeName(target) }));
  };

  /* ───────── άμεσοι διακόπτες (χωρίς «Αποθήκευση») ───────── */
  const markBusy = (key: string, on: boolean) => setBusy(prev => {
    const next = new Set(prev);
    if (on) next.add(key); else next.delete(key);
    return next;
  });

  const applyAvailable = async (key: string, next: boolean) => {
    const v = views[key];
    if (!v) return;
    if (!v.id) {
      setDraft(d => updateProduct(d, baseRef.current, key, s => ({ ...s, is_available: next })));
      return;
    }
    markBusy(key, true);
    try {
      await catalogApi.setAvailability(v.id, next);
      setProducts(ps => ps.map(p => (p.id === v.id ? { ...p, is_available: next } : p)));
      setDraft(d => (d.products[key] ? { ...d, products: { ...d.products, [key]: { ...d.products[key], is_available: next } } } : d));
    } catch (e) {
      showToast('error', errorMessage(e, t('catalog:errors.toggleFailed')));
    } finally {
      markBusy(key, false);
    }
  };

  const applyHidden = async (key: string, next: boolean) => {
    const v = views[key];
    if (!v) return;
    if (!v.id) {
      setDraft(d => updateProduct(d, baseRef.current, key, s => ({ ...s, is_hidden: next })));
      return;
    }
    markBusy(key, true);
    try {
      await catalogApi.setVisibility(v.id, next);
      setProducts(ps => ps.map(p => (p.id === v.id ? { ...p, is_hidden: next } : p)));
      setDraft(d => (d.products[key] ? { ...d, products: { ...d.products, [key]: { ...d.products[key], is_hidden: next } } } : d));
    } catch (e) {
      showToast('error', errorMessage(e, t('catalog:errors.toggleFailed')));
    } finally {
      markBusy(key, false);
    }
  };

  const toggleAvailable = (key: string) => {
    const v = views[key];
    if (!v) return;
    const next = !v.is_available;
    // Κρατικό τέλος: η αυτόματη γραμμή τέλους πλαστικού στηρίζεται σε αυτό — ρώτα πρώτα.
    if (!next && isFeeView(v)) { setFeeConfirm({ key, action: 'unavailable' }); return; }
    applyAvailable(key, next);
  };

  const toggleHidden = (key: string) => {
    const v = views[key];
    if (!v) return;
    const next = !v.is_hidden;
    if (next && isFeeView(v)) { setFeeConfirm({ key, action: 'hide' }); return; }
    applyHidden(key, next);
  };

  const toggleSizeVisibility = async (key: string, size: FormSize) => {
    const v = views[key];
    if (!v?.id || !size.id) return;
    const hide = !size.is_hidden;
    try {
      await catalogApi.setSizeVisibility(size.id, hide);
      setProducts(ps => ps.map(p => (p.id === v.id
        ? { ...p, sizes: (p.sizes || []).map(s => (s.id === size.id ? { ...s, is_hidden: hide } : s)) }
        : p)));
      // Το πρόχειρο κρατά την ίδια απόκρυψη, αλλιώς η επόμενη «Αποθήκευση» θα την ξανάλλαζε.
      setDraft(d => (d.products[key]
        ? updateProductForm(d, baseRef.current, key, f => ({ ...f, sizes: f.sizes.map(s => (s.id === size.id ? { ...s, is_hidden: hide } : s)) }))
        : d));
    } catch (e) {
      showToast('error', errorMessage(e, t('catalog:errors.sizeVisibilityFailed')));
    }
  };

  /** «Προτεινόμενο στο kiosk»: άμεσο, όπως ως σήμερα (και σε κεντρικό κατάλογο)· σε νέο προϊόν με την Αποθήκευση. */
  const toggleKiosk = async (key: string, next: boolean) => {
    const v = views[key];
    if (!v) return;
    if (!v.id) {
      setDraft(d => updateProduct(d, baseRef.current, key, s => ({ ...s, is_kiosk_recommended: next })));
      return;
    }
    markBusy(key, true);
    try {
      await catalogApi.setKioskRecommended(v.id, next);
      setProducts(ps => ps.map(p => (p.id === v.id ? { ...p, is_kiosk_recommended: next } : p)));
      setDraft(d => (d.products[key] ? { ...d, products: { ...d.products, [key]: { ...d.products[key], is_kiosk_recommended: next } } } : d));
    } catch (e) {
      showToast('error', errorMessage(e, t('catalog:errors.toggleFailed')));
    } finally {
      markBusy(key, false);
    }
  };

  const confirmWithdraw = async () => {
    const key = withdrawKey;
    setWithdrawKey(null);
    const v = key ? views[key] : null;
    if (!key || !v?.id) return;
    markBusy(key, true);
    try {
      await catalogApi.withdraw(v.id);
      setProducts(ps => ps.map(p => (p.id === v.id ? { ...p, is_available: false, is_hidden: true } : p)));
      setDraft(d => (d.products[key] ? { ...d, products: { ...d.products, [key]: { ...d.products[key], is_available: false, is_hidden: true } } } : d));
      showToast('success', t('catalog:withdraw.done', { name: v.name }));
    } catch (e) {
      showToast('error', errorMessage(e, t('catalog:withdraw.failed')));
    } finally {
      markBusy(key, false);
    }
  };

  /* ───────── νέα: προϊόν, κατηγορία, ομάδα, υλικό ───────── */
  const categoryDefaults = (key: string) => {
    const form = currentCategoryForm(draftRef.current, baseRef.current, key);
    return form ? { default_product_type: form.default_product_type || null, default_vat: form.default_vat } : null;
  };

  const addProduct = async () => {
    if (central) return;
    let target = node;
    if (target === 'all') target = tree.roots[0] || 'none';
    const ref: CatRef = target === 'none' ? 0 : refOfCatKey(target);
    const groupsForNew = target === 'none' ? [] : await ensureCatGroups(target);
    const key = tempKey('np');
    // Οι προεπιλογές της κατηγορίας (τύπος, ΦΠΑ, ομάδες) μπαίνουν αμέσως — όχι μόνο αν ξαναδιαλεχτεί.
    const form = emptyProductForm(ref, target === 'none' ? null : categoryDefaults(target));
    setDraft(d => addNewProduct(d, key, form, groupsForNew));
    setSearch('');
    setFilter('all');
    setSelection({ kind: 'product', key });
    setChecked([]);
    setPanelTab('basics');
  };

  const duplicateProduct = async (key: string) => {
    const fetched = await ensureLinks([key]);
    const s = currentProduct(draftRef.current, baseRef.current, key);
    if (!s) return;
    const copy = tempKey('np');
    const form = {
      ...s.form,
      name: t('catalog:product.copyName', { name: s.form.name }),
      sizes: s.form.sizes.map(z => ({ ...z, id: undefined, _uid: newUid('ns') })),
      simple_ingredients: s.form.simple_ingredients.map(i => ({ ...i })),
      ingredients: s.form.ingredients.map(i => ({ ...i })),
    };
    setDraft(d => addNewProduct(d, copy, form, (s.groups ?? fetched[key] ?? []).slice()));
    setSelection({ kind: 'product', key: copy });
    setPanelTab('basics');
  };

  const discardNewProduct = (key: string) => {
    setDraft(d => removeNewProduct(d, key));
    if (selection?.key === key) setSelection(null);
  };

  /** Αλλαγή κατηγορίας στη φόρμα· σε ΝΕΟ προϊόν μπαίνουν και οι προεπιλογές της (όπως το παλιό παράθυρο). */
  const changeProductCategory = async (key: string, ref: CatRef) => {
    const isNew = draftRef.current.newProducts.includes(key);
    if (!isNew) {
      setDraft(d => updateProductForm(d, baseRef.current, key, f => ({ ...f, category_id: ref })));
      return;
    }
    const catKey = catKeyOfRef(ref);
    const defaults = catKey === 'none' ? null : categoryDefaults(catKey);
    setDraft(d => updateProductForm(d, baseRef.current, key, f => ({
      ...f,
      category_id: ref,
      ...(defaults?.default_product_type ? { product_type: defaults.default_product_type } : {}),
      ...(defaults && defaults.default_vat !== null && defaults.default_vat !== undefined ? { vat: Number(defaults.default_vat) } : {}),
    })));
    const groupsForNew = catKey === 'none' ? [] : await ensureCatGroups(catKey);
    setDraft(d => updateProduct(d, baseRef.current, key, s => ({ ...s, groups: groupsForNew.slice() })));
  };

  const addCategory = () => {
    if (central) return;
    const key = tempKey('nc');
    setDraft(d => addNewCategory(d, baseRef.current, key, { ...EMPTY_CATEGORY_FORM }, null));
    setSelection({ kind: 'category', key });
    setChecked([]);
    setNode(key);
    setSearch('');
  };

  const createGroup = async (forKey: string, group: NewGroupDraft, alsoOthers: boolean) => {
    const ref = tempKey('ng');
    const owner = views[forKey];
    const others = alsoOthers && owner ? (members[owner.node] || []).filter(k => k !== forKey) : [];
    setDraft(d => updateProduct(addNewGroup(d, ref, group), baseRef.current, forKey, s => ({ ...s, groups: [...(s.groups || []), ref] })));
    if (others.length === 0) return;
    const fetched = await ensureLinks(others);
    setDraft(d => others.reduce((acc, k) => updateProduct(acc, baseRef.current, k, s => {
      const list = s.groups ?? fetched[k];
      // Άγνωστες ομάδες (απέτυχε η φόρτωση): το προϊόν μένει όπως είναι.
      if (!list) return s;
      return list.some(g => String(g) === ref) ? s : { ...s, groups: [...list, ref] };
    }), d));
  };

  const createIngredientDraft = (ingredient: { name: string; price: number; category: string }): IngRef => {
    const ref = tempKey('ni');
    setDraft(d => addNewIngredient(d, ref, ingredient));
    return ref;
  };

  const createSizeGroupDraft = (name: string, items: Array<{ name: string; price_modifier: number; is_default: boolean }>) => {
    setDraft(d => addNewSizeGroup(d, tempKey('nsg'), { name, items }));
    showToast('info', t('catalog:sizes.newGroupQueued', { name }));
  };

  /* ───────── μαζική αλλαγή ───────── */
  const checkedViews = checked.map(k => views[k]).filter(Boolean);
  const feeChecked = checkedViews.filter(isFeeView);

  const bulkFlag = (flag: 'is_available' | 'is_hidden', value: boolean) => {
    // Κρατικά τέλη: εκτός μαζικής απόκρυψης / «μη διαθέσιμα» — η αυτόματη γραμμή τέλους πλαστικού στηρίζεται σε αυτά.
    const risky = (flag === 'is_available' && !value) || (flag === 'is_hidden' && value);
    const targets = risky ? checkedViews.filter(v => !isFeeView(v)) : checkedViews;
    if (risky && targets.length < checkedViews.length) {
      showToast('warning', t('catalog:fees.bulkSkipped', { count: checkedViews.length - targets.length }));
    }
    setDraft(d => targets.reduce((acc, v) => updateProduct(acc, baseRef.current, v.key, s => ({ ...s, [flag]: value })), d));
  };

  const linksLoadedFor = (keys: string[]) => keys.every(k => !k.startsWith('p:') || !!links[k] || !!draft.products[k]?.groups);

  const bulkGroup = (ref: GroupRef, add: boolean) => {
    setDraft(d => checked.reduce((acc, k) => updateProduct(acc, baseRef.current, k, s => {
      // Άγνωστες ομάδες (δεν φορτώθηκαν): το προϊόν μένει όπως είναι.
      const list = s.groups ?? linksRef.current[k];
      if (!list) return s;
      const has = list.some(g => String(g) === String(ref));
      if (add) return has ? s : { ...s, groups: [...list, ref] };
      return has ? { ...s, groups: list.filter(g => String(g) !== String(ref)) } : s;
    }), d));
  };

  /* ───────── αποθήκευση / αναίρεση ───────── */
  const focusProblem = (owner: string, tab?: PanelTab) => {
    if (owner.startsWith('p:') || owner.startsWith('np:')) {
      setSelection({ kind: 'product', key: owner });
      setChecked([]);
      if (tab) setPanelTab(tab);
    } else if (owner.startsWith('c:') || owner.startsWith('nc:')) {
      setSelection({ kind: 'category', key: owner });
      setChecked([]);
    }
  };

  const save = async () => {
    if (saving || central || !storeId) return;
    const problem = validateDraft(draft, base, t);
    if (problem) {
      focusProblem(problem.owner, problem.tab);
      showToast('error', `${problem.title}: ${problem.message}`);
      return;
    }
    setSaving(true);
    const count = changes.length;
    // Οι ομάδες που άλλαξαν συγκρίνονται με ό,τι έχει ο server — όχι με κάτι που δεν φορτώθηκε.
    await ensureLinks(Object.keys(draft.products).filter(k => draft.products[k].groups));
    const result = await saveDraft(draft, { ...base, links: linksRef.current }, {
      storeId, api: catalogApi, t, changeContext: changeCtx, linkOrders: linkOrdersRef.current,
      library: { createOptionGroup, createSizeGroup, createIngredient },
    });
    const rest = remainingDraft(draft, result.saved);
    // Ό,τι δημιουργήθηκε έχει πια πραγματικό κλειδί: η επιλογή ακολουθεί.
    const remap = (key: string) => {
      if (result.saved.products[key] !== undefined) return productKey(result.saved.products[key]);
      if (result.saved.categories[key] !== undefined) return `c:${result.saved.categories[key]}`;
      return key;
    };
    await loadAll(true);
    setSelection(sel => (sel ? { ...sel, key: remap(sel.key) } : sel));
    setNode(n => remap(n));
    setChecked(list => list.map(remap));
    if (result.error) {
      setDraft(rest);
      ensureLinks(Object.keys(rest.products).filter(k => rest.products[k].groups));
      focusProblem(remap(result.error.owner), result.error.tab);
      showToast('error', `${result.error.title}: ${result.error.message}`);
    } else {
      setDraft(emptyDraft());
      setChangesOpen(false);
      showToast('success', t('catalog:bar.saved', { count }));
    }
    setSaving(false);
  };

  const undoAll = () => {
    const sel = selection;
    if (sel && (sel.key.startsWith('np:') || sel.key.startsWith('nc:'))) setSelection(null);
    if (node.startsWith('nc:')) setNode('all');
    setDraft(emptyDraft());
    setChangesOpen(false);
    showToast('info', t('catalog:bar.undone'));
  };

  const undoOne = (entry: ChangeEntry) => {
    if (entry.revert.t === 'newProduct' && selection?.key === entry.revert.key) setSelection(null);
    if (entry.revert.t === 'newCategory') {
      const key = entry.revert.key;
      if (selection?.key === key) setSelection(null);
      if (node === key) setNode('all');
    }
    setDraft(d => applyRevert(d, baseRef.current, entry.revert));
  };

  /* ───────── απόδοση ───────── */
  if (!storeId) return null;

  const segCount = {
    products: Object.keys(views).length,
    options: groups.length + draft.newGroups.length,
    sizes: sizeGroups.length + draft.newSizeGroups.length,
    ingredients: ingredients.length + draft.newIngredients.length,
  };
  const segments: Array<{ id: View; label: string }> = [
    { id: 'products', label: t('catalog:segments.products') },
    { id: 'options', label: t('catalog:segments.options') },
    { id: 'sizes', label: t('catalog:segments.sizes') },
    ...(!warehouse ? [{ id: 'ingredients' as View, label: t('catalog:segments.ingredients') }] : []),
  ];

  const emptyCatalog = load === 'ready' && products.length === 0 && categoryList.filter(c => !c.is_system).length === 0 && !dirty && !manualStart;
  const listTitle = searching ? t('catalog:list.searchTitle') : nodeName(node);
  const listParent = !searching && node !== 'all' && node !== 'none' ? parentKeyOf(node) : null;
  const listCount = searching ? footer.total : node === 'all' ? Object.keys(views).length : node === 'none' ? (members.none || []).length : nodeInfo?.count ?? 0;
  const reorderable = !searching && node !== 'all' && node !== 'none' && !central && filter === 'all';

  const selectedView = selection?.kind === 'product' ? views[selection.key] || null : null;
  const selectedState = selectedView ? currentProduct(draft, base, selectedView.key) : null;

  const renderPanel = () => {
    if (checked.length > 0) {
      return (
        <BulkPanel
          views={checkedViews}
          categoryOptions={categoryOptions}
          groups={groupInfos}
          groupsLoaded={linksLoadedFor(checked)}
          readOnly={central}
          floating={floating}
          feeNames={feeChecked.map(v => v.name)}
          onAvailable={value => bulkFlag('is_available', value)}
          onHidden={value => bulkFlag('is_hidden', value)}
          onMove={ref => moveTo(checked, catKeyOfRef(ref))}
          onColor={tone => setDraft(d => checked.reduce((acc, k) => updateProductForm(acc, baseRef.current, k, f => ({ ...f, color: tone })), d))}
          onGroupAll={ref => bulkGroup(ref, true)}
          onGroupRemove={ref => bulkGroup(ref, false)}
          onClear={() => setChecked([])}
        />
      );
    }
    if (selection?.kind === 'category') {
      const key = selection.key;
      const form = currentCategoryForm(draft, base, key);
      if (!form) return null;
      const parent = parentKeyOf(key);
      const siblings = (parent ? tree.children[parent] || [] : tree.roots).filter(k => !currentCategoryForm(draft, base, k)?.is_hidden);
      return (
        <CategoryPanel
          key={key}
          categoryKey={key}
          form={form}
          isNew={draft.newCategories.includes(key)}
          parentKey={parent}
          parentName={parent ? nodeName(parent) : null}
          parentOptions={tree.roots.filter(k => k !== key).map(k => ({ key: k, name: nodeName(k) }))}
          hasChildren={(tree.children[key] || []).length > 0}
          tone={categoryTone(form.name, form.color, siblings, key)}
          autoTone={categoryTone(form.name, null, siblings, key)}
          groups={draft.categoryGroups[key] ?? base.catGroups[key] ?? (key.startsWith('nc:') ? [] : null)}
          library={groupInfos}
          vatCategories={vatCategories}
          changed={changedFields(changes, key)}
          readOnly={central}
          floating={floating}
          onChange={patch => setDraft(d => updateCategory(d, baseRef.current, key, f => ({ ...f, ...patch })))}
          onParent={parentKey => setDraft(d => {
            const tr = currentTree(d, baseRef.current);
            const end = parentKey ? (tr.children[parentKey] || []).length : tr.roots.length;
            return moveCategory(d, baseRef.current, key, parentKey, end);
          })}
          onGroups={list => setDraft(d => ({ ...d, categoryGroups: { ...d.categoryGroups, [key]: list } }))}
          onClose={closePanel}
          onDiscard={() => { setDraft(d => removeNewCategory(d, baseRef.current, key)); setSelection(null); setNode('all'); }}
        />
      );
    }
    if (selectedView && selectedState) {
      const key = selectedView.key;
      const isNew = selectedView.isNew;
      const orig = isNew ? null : baseProduct(base, key);
      const p = base.byKey[key];
      const catKey = selectedView.node;
      const parent = catKey !== 'none' ? parentKeyOf(catKey) : null;
      return (
        <ProductPanel
          view={selectedView}
          state={selectedState}
          isNew={isNew}
          crumb={{ parent: parent ? nodeName(parent) : undefined, parentTone: parent ? nodes.find(n => n.key === parent)?.tone : null, name: nodeName(catKey) }}
          tab={panelTab}
          onTab={setPanelTab}
          changedTabs={changedTabs(changes, key)}
          changedFields={changedFields(changes, key)}
          readOnly={central}
          floating={floating}
          showIngredients={!warehouse}
          drag={drag}
          basics={{
            feeOptions: {
              isNew,
              originalSizeCount: p?.sizes?.length ?? 0,
              originalFeeCategory: p?.fee_category != null ? Number(p.fee_category) : null,
              originalSizes: orig?.form.sizes,
              originalFeeGross: p?.fee_unit_gross ?? null,
              stateFees,
            },
            categoryOptions,
            vatCategories,
            stateFees,
            suppliers,
            barcodeEnabled: !!store?.barcode_enabled,
            kioskEnabled: !!store?.kiosk_enabled,
            warehouse,
            onCategory: ref => changeProductCategory(key, ref),
            onKiosk: next => toggleKiosk(key, next),
          }}
          sizes={{
            feeGross: selectedView.feeGross,
            sizeGroups,
            onSizeVisibility: size => toggleSizeVisibility(key, size),
            onNewSizeGroup: createSizeGroupDraft,
          }}
          options={{
            groups: groupInfos,
            loaded: isNew || !!links[key] || !!draft.products[key]?.groups,
            categoryName: nodeName(catKey),
            othersInCategory: Math.max(0, (members[catKey] || []).length - 1),
            onCreateGroup: (group, alsoOthers) => createGroup(key, group, alsoOthers),
            onOpenLibrary: () => switchView('options'),
          }}
          ingredients={{
            library: ingredients,
            newIngredients: draft.newIngredients,
            onCreateIngredient: createIngredientDraft,
          }}
          onChange={fn => setDraft(d => updateProduct(d, baseRef.current, key, fn))}
          onClose={closePanel}
          onDuplicate={() => duplicateProduct(key)}
          onMove={() => { setPanelTab('basics'); setTimeout(() => document.getElementById('prod-cat')?.focus(), 0); }}
          onWithdraw={() => setWithdrawKey(key)}
          onDiscard={() => discardNewProduct(key)}
        />
      );
    }
    return null;
  };

  const treeProps = {
    nodes,
    allCount: Object.keys(views).length,
    noneCount: (members.none || []).length,
    selected: node,
    expanded,
    edited,
    readOnly: central,
    lockTitle,
    drag,
    onSelect: selectNode,
    onToggle: toggleExpanded,
    onEdit: editCategory,
    onAdd: addCategory,
    onMoveCategory: (key: string, parentKey: string | null, index: number) => setDraft(d => moveCategory(d, baseRef.current, key, parentKey, index)),
    onDropProducts: (keys: string[], target: string) => moveTo(keys, target),
  };

  const withdrawView = withdrawKey ? views[withdrawKey] : null;
  const feeView = feeConfirm ? views[feeConfirm.key] : null;

  return (
    <div
      ref={pageRef}
      className="cx-page"
      style={{ height: `calc(var(--app-vh, 100vh) - ${top}px - 20px)`, paddingBottom: dirty ? 66 : 0 }}
      data-testid="catalog-page"
    >
      <div className="cx-head">
        <h1>{t('catalog:title')}</h1>
        <div className="cx-seg" role="tablist" aria-label={t('catalog:title')}>
          {segments.map(s => (
            <button key={s.id} type="button" role="tab" aria-selected={view === s.id} onClick={() => switchView(s.id)}>
              {s.label}
              {load === 'ready' && <span className="cx-n">{segCount[s.id]}</span>}
            </button>
          ))}
        </div>
        <span className="cx-sp" />
        {!central && (
          <button type="button" className="cx-btn ghost sm" onClick={() => setImportOpen(true)} disabled={dirty || load !== 'ready'}
            title={dirty ? t('catalog:import.saveFirst') : t('catalog:import.title')}>
            <Upload className="cx-ico" /> {t('catalog:import.button')}
          </button>
        )}
      </div>

      {central && (
        <div className="cx-lock" role="note">
          <Package className="h-5 w-5 text-amber-700 dark:text-amber-400 shrink-0" aria-hidden="true" style={{ marginTop: 2 }} />
          <div className="text-sm">
            <p className="font-semibold text-amber-800 dark:text-amber-300">{t('catalog:central.title')}</p>
            <p className="text-amber-900/80 dark:text-amber-200/80">{t('catalog:central.hint')} {t('catalog:central.operational')}</p>
          </div>
        </div>
      )}

      {load === 'loading' && (
        <div className="cx-pane" style={{ flex: 1 }}>
          <div className="cx-empty" role="status"><Loader2 className="cx-ico animate-spin" />{t('catalog:common.loading')}</div>
        </div>
      )}
      {load === 'error' && (
        <div className="cx-pane" style={{ flex: 1 }}>
          <div className="cx-empty" role="alert">
            <b>{t('catalog:errors.loadTitle')}</b>
            <span>{t('catalog:errors.loadHint')}</span>
            <button type="button" className="cx-btn sm" onClick={() => loadAll()}><RefreshCw className="cx-ico" /> {t('catalog:common.retry')}</button>
          </div>
        </div>
      )}

      {load === 'ready' && view === 'options' && <div className="cx-lib"><OptionGroupsTab /></div>}
      {load === 'ready' && view === 'sizes' && <div className="cx-lib"><SizeGroupsTab /></div>}
      {load === 'ready' && view === 'ingredients' && !warehouse && <div className="cx-lib"><IngredientsTab /></div>}

      {load === 'ready' && view === 'products' && emptyCatalog && (
        <div className="cx-pane" style={{ flex: 1, overflow: 'auto' }}>
          {central
            ? <div className="cx-empty"><Package className="cx-ico" /><b>{t('catalog:central.empty')}</b></div>
            : (
              <>
                <CatalogImportWizard onImportComplete={() => loadAll()} />
                <div className="cx-empty" style={{ paddingTop: 0 }}>
                  <button type="button" className="cx-btn ghost sm" onClick={() => setManualStart(true)}>{t('catalog:import.manualStart')}</button>
                </div>
              </>
            )}
        </div>
      )}

      {load === 'ready' && view === 'products' && !emptyCatalog && (
        <div
          ref={setWsEl}
          className={saving ? 'cx-ws cx-busy' : 'cx-ws'}
          style={{ gridTemplateColumns: columns }}
          aria-busy={saving}
          onKeyDown={e => { if (e.key === 'Escape' && drawer) setDrawer(false); }}
        >
          {treeCollapsed
            ? <TreeRail onOpen={() => setDrawer(true)} />
            : <CatalogTree {...treeProps} variant="pane" onCollapse={() => setTreeMode('closed')} />}
          <ProductList
            title={listTitle}
            crumb={searching ? t('catalog:list.searchCrumb') : listParent ? (<><ToneDot tone={nodes.find(n => n.key === listParent)?.tone} />{nodeName(listParent)}</>) : t('catalog:title')}
            count={listCount}
            tone={!searching && nodeInfo ? nodeInfo.tone : undefined}
            chooser={treeCollapsed}
            onOpenChooser={() => setDrawer(true)}
            sections={sections}
            showContext={searching || node === 'all'}
            contextOf={v => categoryPath(refOfCatKey(v.node))}
            groupsOf={v => (v.groups ? v.groups.map(groupName).join(' · ') || '—' : null)}
            edited={edited}
            selectedKey={selection?.kind === 'product' ? selection.key : null}
            checked={checked}
            search={search}
            onSearch={setSearch}
            filter={filter}
            onFilter={setFilter}
            readOnly={central}
            lockTitle={lockTitle}
            canAdd
            onAdd={addProduct}
            onOpen={openProduct}
            onToggleCheck={toggleCheck}
            onSetChecked={setChecked}
            onToggleAvailable={toggleAvailable}
            onToggleHidden={toggleHidden}
            busy={busy}
            reorderable={reorderable}
            onReorder={(n, keys) => setDraft(d => setProductOrder(d, n, keys))}
            onMoveTo={(keys, n, index) => moveTo(keys, n, index)}
            drag={drag}
            footer={footer}
          />
          {renderPanel()}
          {treeCollapsed && drawer && (
            <>
              <div className="cx-scrim" onClick={() => setDrawer(false)} aria-hidden="true" />
              <CatalogTree {...treeProps} variant="drawer" onPin={() => { setTreeMode('open'); setDrawer(false); }} onClose={() => setDrawer(false)} />
            </>
          )}
        </div>
      )}

      {!central && (
        <CatalogSaveBar
          changes={changes}
          owners={changeOwners(changes)}
          saving={saving}
          open={changesOpen}
          onToggle={() => setChangesOpen(o => !o)}
          onUndoAll={undoAll}
          onUndo={undoOne}
          onSave={save}
        />
      )}

      <ConfirmModal
        isOpen={!!withdrawView}
        variant="warning"
        title={t('catalog:withdraw.confirmTitle', { name: withdrawView?.name || '' })}
        message={`${t('catalog:withdraw.confirmMessage')}${isFeeView(withdrawView) ? ` ${t('catalog:fees.withdrawWarning')}` : ''}`}
        confirmLabel={t('catalog:withdraw.button')}
        cancelLabel={t('catalog:common.cancel')}
        onConfirm={confirmWithdraw}
        onCancel={() => setWithdrawKey(null)}
      />
      <ConfirmModal
        isOpen={!!feeView}
        variant="warning"
        title={t('catalog:fees.confirmTitle', { name: feeView?.name || '' })}
        message={feeConfirm?.action === 'hide' ? t('catalog:fees.confirmHide') : t('catalog:fees.confirmUnavailable')}
        confirmLabel={feeConfirm?.action === 'hide' ? t('catalog:fees.confirmHideButton') : t('catalog:fees.confirmUnavailableButton')}
        cancelLabel={t('catalog:common.cancel')}
        onConfirm={() => {
          const fc = feeConfirm;
          setFeeConfirm(null);
          if (!fc) return;
          if (fc.action === 'hide') applyHidden(fc.key, true); else applyAvailable(fc.key, false);
        }}
        onCancel={() => setFeeConfirm(null)}
      />

      {importOpen && (
        <div className="role-overlay fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setImportOpen(false)}>
          <div className="role-dialog bg-surface-1 rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-auto shadow-2xl relative" onClick={e => e.stopPropagation()}>
            <button type="button" className="cx-icon-btn" style={{ position: 'absolute', top: 10, right: 10 }} onClick={() => setImportOpen(false)}
              aria-label={t('catalog:common.close')}>
              <X className="cx-ico" />
            </button>
            <CatalogImportWizard reimport onClose={() => setImportOpen(false)} onImportComplete={() => { setImportOpen(false); loadAll(); }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogPage;
